import { useState, useMemo, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import PageTransition from '@/components/ui/PageTransition';
import {
  useListServicesQuery,
  useUpdateServiceMutation,
  type Service,
  type ListClientServicesParams
} from '@/api/mcp-appointments';
import { useEffectiveCompanyId } from '@/contexts/AdminClientsContext';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { CreateServiceModal } from '@/components/services/CreateServiceModal';
import { DeleteServiceModal } from '@/components/services/DeleteServiceModal';
import { ServiceRolesDialog } from '@/components/service-roles';
import {
  ServicesHeader,
  ServicesStats,
  ServicesFilters,
  ServicesTable,
  type ServicesFilterState
} from '@/components/services/list';
import { serviceHasDefinedRequirements } from '@/utils/serviceRequirements';
import { hasValidActivationClientInfo } from '@/utils/serviceForm';
import { createManageRouteGuard } from '@/utils/manageRouteGuards';
import { TENANT_ROLES } from '@/constants/tenantRoles';
import { useClientListFilters } from '@/hooks/useClientListFilters';

export const Route = createFileRoute('/manage/company-services/')({
  beforeLoad: createManageRouteGuard({ requiredTenantRoles: [TENANT_ROLES.OWNER] }),
  component: ServicesPage
});

/** Default filters of the services list; also what "clear all" restores. */
const INITIAL_FILTERS: ServicesFilterState = { searchText: '', activeFilter: 'all' };

function ServicesPage() {
  const { t } = useTranslation('manage');
  const { message } = App.useApp();
  const effectiveCompanyId = useEffectiveCompanyId();
  const updateService = useUpdateServiceMutation();

  const { filters, updateFilter, resetFilters, hasActiveFilters, currentPage, pageSize, paginate, handlePageChange } =
    useClientListFilters<ServicesFilterState>(INITIAL_FILTERS);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceRolesDialogOpen, setServiceRolesDialogOpen] = useState(false);
  const [togglingServiceId, setTogglingServiceId] = useState<string | null>(null);
  const togglingServiceIdRef = useRef<string | null>(null);

  const queryParams: ListClientServicesParams = useMemo(
    () => ({
      companyId: effectiveCompanyId || undefined,
      name: filters.searchText || undefined,
      isActive:
        filters.activeFilter === 'all'
          ? undefined
          : filters.activeFilter === 'active'
    }),
    [effectiveCompanyId, filters.searchText, filters.activeFilter]
  );

  const { data: services, isLoading } = useListServicesQuery(queryParams, true);

  const stats = useMemo(() => {
    if (!services) {
      return { total: 0, active: 0, inactive: 0, isLoading };
    }
    const active = services.filter((s) => s.isActive).length;
    return {
      total: services.length,
      active,
      inactive: services.length - active,
      isLoading
    };
  }, [services, isLoading]);

  const paginatedServices = paginate(services);

  const handleToggleActive = async (service: Service, isActive: boolean) => {
    if (togglingServiceIdRef.current !== null) {
      return;
    }
    if (
      isActive &&
      !hasValidActivationClientInfo(
        service.requiredUserInfo,
        service.fulfillmentMode ?? 'APPOINTMENT'
      )
    ) {
      message.error(t('services.activeToggle.noClientContact'));
      return;
    }
    if (
      isActive &&
      service.fulfillmentMode !== 'CALLBACK' &&
      !serviceHasDefinedRequirements(service)
    ) {
      message.error(t('services.activeToggle.noRoles'));
      return;
    }
    togglingServiceIdRef.current = service.id;
    setTogglingServiceId(service.id);
    try {
      await updateService.mutateAsync({
        serviceId: service.id,
        body: { isActive }
      });
      message.success(t('services.activeToggle.success'));
    } catch (error) {
      message.error(t('services.activeToggle.error'));
      console.error('Toggle service active error:', error);
    } finally {
      if (togglingServiceIdRef.current === service.id) {
        togglingServiceIdRef.current = null;
      }
      setTogglingServiceId((current) =>
        current === service.id ? null : current
      );
    }
  };

  return (
    <PageTransition animation="fadeInUp">
      <ServicesHeader
        onAddClick={() => setCreateModalOpen(true)}
        onManageRolesClick={() => setServiceRolesDialogOpen(true)}
      />
      <ServicesStats stats={stats} />
      <ServicesFilters
        filters={filters}
        filteredCount={services?.length ?? 0}
        hasActiveFilters={hasActiveFilters}
        onFilterChange={updateFilter}
        onReset={resetFilters}
      />

      <ServicesTable
        services={paginatedServices}
        total={services?.length ?? 0}
        hasActiveFilters={hasActiveFilters}
        isLoading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        togglingServiceId={togglingServiceId}
        onPageChange={handlePageChange}
        onResetFilters={resetFilters}
        onDeleteService={(s) => {
          setSelectedService(s);
          setDeleteModalOpen(true);
        }}
        onToggleActive={handleToggleActive}
      />

      <CreateServiceModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />

      <DeleteServiceModal
        open={deleteModalOpen}
        service={selectedService}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedService(null);
        }}
      />

      <ServiceRolesDialog
        open={serviceRolesDialogOpen}
        onClose={() => setServiceRolesDialogOpen(false)}
      />
    </PageTransition>
  );
}
